import React, { useCallback, useMemo } from "react";
import classNames from "classnames";

// core deps
import {
    GenericError,
    getCloudinaryUrl,
    isAndroidPlatform,
    isEmpty,
    isiOSPlatform,
    isMWebPlatform,
    withStyles,
} from "@swiggy-private/core-ui";
import {
    getBridgeSdk,
    getNativeChannelDeepLink,
    GlobalHeaderState,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";
import {
    NavigationScreenLinkTypes,
    NavigateToScreenRequestPayload,
    NavigationTypes,
} from "@swiggy-private/native-bridge";

// constants
import { POD_ORDER_ALREADY_PAID, POD_ORDER_CANCELLED } from "@payment-client/constants/StatusCodes";
import { GO_TO_HOME, OOPS_SOMETHING_WENT_WRONG, RETRY, TRACK_ORDER } from "@payment-client/constants/TextDict";
import { AUTOPAY_ERROR_SCREEN_TEST_IDS } from "@swiggy-private/payment-test-ids";

// styles
import styles from "./styles.scss";

export interface AutoPayErrorScreenProps {
    statusCode: number;
    title?: string;
    message?: string;
}

export const ERROR_IMAGE_IDS: Record<number, string> = {
    [POD_ORDER_ALREADY_PAID]: "4_0_Assets/churn_mix",
    [POD_ORDER_CANCELLED]: "4_0_Assets/bee_vase",
};

export const ERROR_CTA_TEXT: Record<number, string> = {
    [POD_ORDER_CANCELLED]: GO_TO_HOME,
    [POD_ORDER_ALREADY_PAID]: TRACK_ORDER,
};

const AutoPayErrorScreen: React.FunctionComponent<AutoPayErrorScreenProps> = (props) => {
    const { message = OOPS_SOMETHING_WENT_WRONG, title, statusCode } = props;

    const errorContainerClass = classNames(styles.error);

    const goToHome = useCallback((): void => {
        const platform = (GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "").toLowerCase();
        let payload;

        // in iOS there's a bug due to which deeplink doesnt work so we use SCREEN to navigate
        // check for isMwebPlatform condition with team
        if (isiOSPlatform(platform) || isMWebPlatform(platform)) {
            payload = {
                type: NavigationTypes.SCREEN,
                link: NavigationScreenLinkTypes.HOME,
            };
        } else if (isAndroidPlatform(platform)) {
            // android has other issues so we cant use above approach in payment webview
            payload = {
                type: NavigationTypes.DEEPLINK,
                link: getNativeChannelDeepLink("swiggy://home"),
            };
        }

        if (payload) {
            getBridgeSdk()
                .navigateToScreen(payload as NavigateToScreenRequestPayload)
                .catch(() => void 0);
        }
    }, []);

    const goToTrackScreen = useCallback(() => {
        const orderIdFromHeader = GlobalHeaderState.getHeader(SwgyHeaderKeysList.orderid);

        if (!isEmpty(orderIdFromHeader)) {
            const data: NavigateToScreenRequestPayload = {
                type: NavigationTypes.SCREEN,
                link: NavigationScreenLinkTypes.TRACK,
                params: {
                    orderId: orderIdFromHeader,
                    hideMakePaymentButton: true, // tell native to hide the make payment button for autopay
                    shouldCloseWebview: true,
                },
            };

            getBridgeSdk()
                .navigateToScreen(data)
                .catch(() => void 0);
        }
    }, []);

    const onClickPrimaryAction = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();

            switch (statusCode) {
                case POD_ORDER_ALREADY_PAID: {
                    goToTrackScreen();
                    return;
                }
                case POD_ORDER_CANCELLED: {
                    goToHome();
                    return;
                }
                default:
                    window.location.reload();
                    return;
            }
        },
        [goToHome, goToTrackScreen, statusCode],
    );

    const ctaText = useMemo(() => {
        const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";

        // TODO: temp hack, since in IOS the native bridge is taking user to
        // track screen we change cta to convey the same
        if (isiOSPlatform(platform)) {
            return TRACK_ORDER;
        }

        return ERROR_CTA_TEXT[statusCode] ?? RETRY;
    }, [statusCode]);

    const getImageUrl = useCallback((): string => {
        const imageId = ERROR_IMAGE_IDS[statusCode];

        return getCloudinaryUrl(imageId, {
            trim: false,
        });
    }, [statusCode]);

    // For Generic Error which we dont handle gracefully render the error screen
    if (![POD_ORDER_ALREADY_PAID, POD_ORDER_CANCELLED].includes(statusCode)) {
        return <GenericError />;
    }

    return (
        <div className={errorContainerClass}>
            <div className={styles.errorContent}>
                <img
                    className={styles.errorImage}
                    crossOrigin="anonymous"
                    src={getImageUrl()}
                    alt={title}
                    data-testid={AUTOPAY_ERROR_SCREEN_TEST_IDS.IMAGE}
                />
                <div className={styles.errorTextContent}>
                    {title && (
                        <div className={styles.errorTitle} data-testid={AUTOPAY_ERROR_SCREEN_TEST_IDS.TITLE}>
                            {title}
                        </div>
                    )}
                    <div className={classNames(styles.errorMsg)} data-testid={AUTOPAY_ERROR_SCREEN_TEST_IDS.MESSAGE}>
                        {message}
                    </div>
                </div>

                <div className={styles.errorActionContainer}>
                    <button
                        className={classNames(styles.errorAction)}
                        onClick={onClickPrimaryAction}
                        data-testid={AUTOPAY_ERROR_SCREEN_TEST_IDS.CTA}
                    >
                        {ctaText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default withStyles(AutoPayErrorScreen, styles);
